/**
 * View Component: SkeletonCard
 * Loading skeleton for post cards while data is loading.
 *
 * Props:
 *   count: number of skeleton cards to render
 *
 * T-Cron-2026-10-31 08:00 早间 (T123) — 改进清单 #10「无障碍」。
 *
 * 取证:`grep -c 'aria-label' vite/src/components/*.jsx` → 本文件 **0 命中**,
 * 是全树 aria-label 覆盖率最低的两个文件之一(另一个是 ErrorBoundary.jsx)。
 *
 * 为什么这是个真缺口而不是洁癖:
 *   骨架屏是**加载中**的唯一视觉呈现。此前它对辅助技术是完全隐形的:
 *     · 没有 role="status" / aria-live ⇒ 读屏用户点开看板后,
 *       听到的是"空",不知道是"还在加载"还是"没有内容";
 *     · 没有 aria-busy ⇒ 屏幕阅读器不会把整块区域标成"忙碌",
 *       用户没理由等待;
 *     · 每张骨架卡没有 aria-hidden ⇒ 读屏软件会尝试去念那些
 *       纯装饰的灰色矩形(它们没有文本节点,所以多数读屏会跳过,
 *       但不保证 —— 某些读屏会把空 div 当作分隔符念出来)。
 *
 * 修法(遵循「装饰性内容对辅助技术隐藏 + 状态用 live region 播报」):
 *   ① 容器:role="status" + aria-live="polite" + aria-busy="true"
 *      + aria-label —— 加载开始时 polite 播报一次「正在加载看板…」,
 *      不打断用户正在读的内容(不用 assertive)。
 *   ② 每张骨架卡:aria-hidden="true" —— 它们是纯装饰,不该被念出来。
 *   ③ 保留视觉完全不变:只加 ARIA 属性,不加任何 DOM 结构变化,
 *      所以 CSS(.skeleton-grid / .skeleton-card)的布局不受影响。
 *
 * 为什么不用 aria-label 在每张卡上:
 *   6 张卡念 6 遍「正在加载」是噪音;一次容器播报就够。
 */

/**
 * @param {object} props
 * @param {string} [props.label]  读屏播报文案;默认 "正在加载看板内容"
 */
function SkeletonCard({ label }) {
  return (
    // aria-hidden: 纯装饰的灰色矩形,不该被读屏软件念出来。
    // label 只用在容器上,这里不重复。
    <div className="skeleton-card" aria-hidden="true" data-skeleton-label={label || ''}>
      <div className="skeleton-cover"></div>
      <div className="skeleton-body">
        <div className="skeleton-line short"></div>
        <div className="skeleton-line long"></div>
        <div className="skeleton-line medium"></div>
        <div className="skeleton-line short"></div>
      </div>
    </div>
  );
}

/**
 * @param {object} props
 * @param {number} [props.count]     骨架卡数量
 * @param {string} [props.label]     读屏播报文案
 */
function SkeletonGrid({ count = 6, label = '正在加载看板内容' }) {
  return (
    <div
      className="skeleton-grid"
      // role="status" + aria-live="polite":加载状态用 polite 而不是
      // assertive —— 加载不该打断用户正在听的内容。
      role="status"
      aria-live="polite"
      // aria-busy="true":告诉辅助技术这块区域还在更新中。
      aria-busy="true"
      aria-label={label}
    >
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonCard key={i} />
      ))}
    </div>
  );
}

window.SkeletonCard = SkeletonCard;

export default SkeletonGrid;
window.SkeletonGrid = SkeletonGrid;
